import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Col, Container, Div, Image, Input, Row, Text } from '@pinpoint/design-kit';
import { connect } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';

import CONSTANTS from 'src/store/constants';
import { authActions } from 'src/store/actions';
import { ErrorText, Button } from 'src/components';

const defaultValues = {
  newPassword: '',
  confirmPassword: '',
};

function PasswordChallenge({
  history,
  challengeName,
  session,
  username,
  passwordChallengeResponseStatus,
  passwordChallengeResponseError,
  passwordChallengeResponse,
}) {
  useEffect(() => {
    if (!challengeName || !session || !username) {
      history.push('/login');
    }
  }, [challengeName, session, history, username]);

  const { handleSubmit, errors, control, setError, watch } = useForm({ defaultValues });

  const [errorMessage, setErrorMessage] = useState('');

  const onSubmit = useCallback(
    (data) => {
      setErrorMessage('');
      const { newPassword, confirmPassword } = data;
      if (confirmPassword === newPassword) {
        passwordChallengeResponse(challengeName, session, username, confirmPassword);
      } else {
        setError('confirmPassword', 'value', "The passwords don't match");
      }
    },
    [challengeName, passwordChallengeResponse, session, setError, username]
  );

  useEffect(() => {
    if (passwordChallengeResponseStatus === CONSTANTS.API.SUCCEEDED) {
      window.location = '/';
    } else if (passwordChallengeResponseStatus === CONSTANTS.API.FAILED) {
      setErrorMessage(
        'Password should contain uppercase and lowercase characters, a number and one of the following special characters ^$.[]{}()?"!@#%&/\\,><\':;|_~. If you continue to see errors when changing your password, please contact Pinpoint.'
      );
    }
  }, [passwordChallengeResponseStatus, passwordChallengeResponseError]);

  return (
    <Div
      minW="100vw"
      w="100%"
      minH="100vh"
      h="100%"
      d="flex"
      justify="center"
      align="center"
      p={{ x: '1rem', y: '4rem' }}
      pos="relative"
    >
      <Image
        src="/static/login-back.png"
        srcSet="/static/login-back@2x.png 2x,/static/login-back@3x.png 3x,"
        alt="background"
        w="100vw"
        h="100vh"
        pos="absolute"
        top="0"
        style={{
          objectFit: 'cover',
        }}
      />
      <Div
        w="480px"
        d="flex"
        flexDirection="column"
        align="center"
        shadow="5"
        p={{ x: '3rem', t: '4rem', b: '2rem' }}
        rounded="md"
        bg="black50"
        zIndex="10"
        style={{
          backdropFilter: 'blur(8px)',
        }}
      >
        <Container>
          <Image src="/static/on-point.svg" />
          <form
            onSubmit={(e) => {
              e.preventDefault();
            }}
          >
            <Row p={{ y: '1rem' }} d="flex" justify="center">
              <Col>
                <Div d="flex" align="center">
                  <ErrorText shake={errorMessage !== ''}>{errorMessage}</ErrorText>
                </Div>
              </Col>
            </Row>
            <Row p={{ y: '1rem' }}>
              <Col size={{ xs: '12' }}>
                <Text textSize="subtitle" textColor="gray400">
                  It looks like you are logging in for the first time. Please set up a new password to continue
                </Text>
              </Col>
            </Row>
            <Row p={{ y: '1rem' }}>
              <Col size={{ xs: '12' }}>
                <Div d="flex" align="center" minH="2.5rem">
                  <Text textSize="subtitle" textColor="gray400">
                    New Password
                  </Text>
                </Div>
              </Col>
              <Col size={{ xs: '12' }}>
                <Controller
                  name="newPassword"
                  as={Input}
                  type="password"
                  control={control}
                  rules={{
                    required: 'New Password is required',
                    minLength: {
                      value: 10,
                      message: 'Password must contain at least 10 characters',
                    },
                    maxLength: {
                      value: 99,
                      message: 'Password has to be less than 100 characters',
                    },
                    validate: (value) =>
                      (/[a-z]/.test(value) &&
                        /[A-Z]/.test(value) &&
                        /[0-9]/.test(value) &&
                        /[\^$.[\]{}()?"!@#%&/\\,><':;|_~]/.test(value)) ||
                      'Password should contain uppercase and lowercase characters, a number and one of the following special characters [^$.[]{}()?"!@#%&/\\,><\':;|_~]',
                  }}
                  onKeyDown={(event) => {
                    if (event.keyCode === 13) {
                      handleSubmit(onSubmit)(event);
                    }
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col size={{ xs: '12' }}>
                <Text textColor="danger600" textSize="caption">
                  <ErrorMessage errors={errors} name="newPassword" />
                </Text>
              </Col>
            </Row>
            <Row p={{ y: '1rem' }}>
              <Col size={{ xs: '12' }}>
                <Div d="flex" align="center" minH="2.5rem">
                  <Text textSize="subtitle" textColor="gray400">
                    Confirm Password
                  </Text>
                </Div>
              </Col>
              <Col size={{ xs: '12' }}>
                <Controller
                  name="confirmPassword"
                  as={Input}
                  control={control}
                  type="password"
                  rules={{
                    required: 'Password is required',
                    validate: (value) => value === watch('newPassword') || "The passwords don't match",
                  }}
                  onKeyDown={(event) => {
                    if (event.keyCode === 13) {
                      handleSubmit(onSubmit)(event);
                    }
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col size={{ xs: '12' }}>
                <Text textColor="danger800" textSize="caption">
                  <ErrorMessage errors={errors} name="confirmPassword" />
                </Text>
              </Col>
            </Row>
          </form>
          <Div p={{ y: '1rem' }} d="flex" justify="center">
            <Button
              buttonStyle="primary"
              onClick={handleSubmit(onSubmit)}
              onKeyDown={(event) => {
                event.preventDefault();
                if (event.keyCode === 13) {
                  handleSubmit(onSubmit)(event);
                }
              }}
              isLoading={passwordChallengeResponseStatus === CONSTANTS.API.STARTED}
            >
              SET PASSWORD
            </Button>
          </Div>
          <Div d="flex" justify="space-between" align="center">
            <Div p={{ y: '1rem' }} d="flex" justify="center">
              <Image w="90px" h="2rem" src="/static/powered-by-pinpoint-white.svg" />
            </Div>
            <Div h="1.5rem" border={{ r: '1px solid' }} borderColor="gray400" />
            <Div p={{ y: '1rem' }} d="flex" justify="center">
              <Text
                textDecor="underline"
                textSize="caption"
                textColor="gray400"
                cursor="pointer"
                onClick={() => {
                  window.open('https://pinpointpredictive.com/tos/', '_blank') ||
                    window.location.replace('https://pinpointpredictive.com/tos/');
                }}
              >
                Terms of Service
              </Text>
            </Div>
            <Div h="1.5rem" border={{ r: '1px solid' }} borderColor="gray400" />
            <Div p={{ y: '1rem' }} d="flex" justify="center">
              <Text
                textDecor="underline"
                textSize="caption"
                textColor="gray400"
                cursor="pointer"
                onClick={() => {
                  history.push('/login');
                }}
              >
                Login
              </Text>
            </Div>
          </Div>
        </Container>
      </Div>
    </Div>
  );
}

const mapStateToProps = ({ auth }) => ({
  ...auth,
});

const mapDispatchToProps = (dispatch) => ({
  login: (...params) => dispatch(authActions.login(...params)),
  passwordChallengeResponse: (...params) => dispatch(authActions.passwordChallengeResponse(...params)),
});

PasswordChallenge.propTypes = {
  challengeName: PropTypes.oneOfType([PropTypes.string, undefined]).isRequired,
  session: PropTypes.oneOfType([PropTypes.string, undefined]).isRequired,
  username: PropTypes.oneOfType([PropTypes.string, undefined]).isRequired,
  passwordChallengeResponseStatus: PropTypes.oneOfType([PropTypes.string, undefined]).isRequired,
  passwordChallengeResponseError: PropTypes.oneOfType([PropTypes.string, undefined]).isRequired,
  passwordChallengeResponse: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(PasswordChallenge);
